import {
  CLINIC_NAME,
  CLINIC_LOCATION,
  CLINIC_STATE,
  EMAIL,
  PHONE_DISPLAY,
} from '../constants';

const VALUES = [
  { icon: '⭐', title: 'Excellence', desc: 'Premium spaces, premium care — we set a new standard for healthcare in Siddipet.' },
  { icon: '🤝', title: 'Trust', desc: 'Every patient and practitioner can rely on us for consistency and integrity.' },
  { icon: '💚', title: 'Compassion', desc: 'Healthcare is deeply personal. We treat every individual with dignity and empathy.' },
  { icon: '🔬', title: 'Innovation', desc: 'Modern equipment and forward-thinking design at every touchpoint.' },
];

export default function About({ onNav }) {
  const go = (id) => { onNav(id); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  return (
    <>
      <style>{`
        .about-hero {
          padding: 120px 0 64px; background: var(--charcoal);
          position: relative; overflow: hidden;
        }
        .about-hero::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(circle at 70% 40%, rgba(30,58,95,0.08) 0%, transparent 60%);
        }
        .about-hero-inner { position: relative; }
        .about-story { background: var(--white); }
        .story-grid {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 56px; align-items: center;
        }
        .story-p { font-size: 15px; color: var(--text-mid); line-height: 1.8; margin-bottom: 14px; }
        .story-visual {
          background: var(--sand); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 32px;
        }
        .story-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 20px; }
        .s-stat {
          background: var(--white); border: 1px solid var(--border);
          border-radius: var(--radius-sm); padding: 18px; text-align: center;
        }
        .s-stat-val {
          font-family: var(--font-display); font-size: 28px; font-weight: 600;
          color: var(--terra); display: block; margin-bottom: 3px;
        }
        .s-stat-lbl { font-size: 11px; color: var(--text-muted); }
        .story-quote {
          padding: 18px; border-left: 2px solid var(--terra);
          background: var(--terra-pale); border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
        }
        .story-quote-text {
          font-family: var(--font-display); font-size: 16px; font-style: italic;
          color: var(--charcoal); line-height: 1.6;
        }
        .story-quote-attr { font-size: 11px; color: var(--terra); margin-top: 8px; display: block; }

        .values-section { background: var(--sand-mid); }
        .vals-grid {
          display: grid; grid-template-columns: repeat(4,1fr); gap: 18px; margin-top: 36px;
        }
        .val-card {
          background: var(--white); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 24px; text-align: center; transition: all 0.2s;
        }
        .val-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); border-color: rgba(30,58,95,0.2); }
        .val-icon { font-size: 28px; margin-bottom: 12px; }
        .val-title { font-size: 14.5px; font-weight: 600; color: var(--charcoal); margin-bottom: 7px; }
        .val-desc { font-size: 13px; color: var(--text-muted); line-height: 1.65; }

        .launch-section { background: var(--white); }
        .launch-card {
          max-width: 600px; margin: 0 auto; text-align: center;
          background: var(--sand); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 48px 36px;
        }
        .launch-badge {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 5px 13px; border-radius: 20px;
          border: 1px solid rgba(30,58,95,0.25); background: var(--terra-pale);
          font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
          color: var(--terra); font-weight: 500; margin-bottom: 20px;
        }
        .launch-dot { width: 6px; height: 6px; background: var(--terra); border-radius: 50%; animation: pulse-dot 2s infinite; }
        .launch-title {
          font-family: var(--font-display); font-size: 32px; font-weight: 600;
          color: var(--charcoal); margin-bottom: 12px;
        }
        .launch-desc { font-size: 14px; color: var(--text-muted); line-height: 1.75; margin-bottom: 28px; }
        .launch-contacts {
          display: flex; justify-content: center; gap: 32px; flex-wrap: wrap;
          padding-top: 24px; border-top: 1px solid var(--border); margin-top: 8px;
        }
        .lc-lbl { font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--terra); margin-bottom: 3px; display: block; }
        .lc-val { font-size: 13px; color: var(--text-mid); }

        @media (max-width: 900px) {
          .story-grid { grid-template-columns: 1fr; gap: 28px; }
          .vals-grid { grid-template-columns: repeat(2,1fr); }
        }
        @media (max-width: 540px) {
          .vals-grid { grid-template-columns: 1fr; }
          .launch-card { padding: 32px 20px; }
        }
      `}</style>

      {/* Hero */}
      <div className="about-hero">
        <div className="container about-hero-inner">
          <span className="eyebrow" style={{color:'var(--accent)'}}>About Us</span>
          <h1 style={{fontFamily:'var(--font-display)',fontSize:'clamp(34px,5vw,54px)',fontWeight:600,color:'white',lineHeight:1.1,letterSpacing:'-0.01em',marginBottom:14,maxWidth:520}}>
            Where healthcare<br /><em style={{fontStyle:'italic',color:'var(--accent)'}}>meets excellence.</em>
          </h1>
          <p style={{fontSize:15,color:'rgba(255,255,255,0.55)',maxWidth:480,lineHeight:1.75}}>
            A premium polyclinic built on the belief that quality healthcare should feel as good as it works — Siddipet, 2026.
          </p>
        </div>
      </div>

      {/* Story */}
      <section className="section about-story">
        <div className="container">
          <div className="story-grid">
            <div>
              <span className="eyebrow">Our Story</span>
              <h2 className="section-title" style={{marginBottom:18}}>Built from a vision<br />of better healthcare</h2>
              <p className="story-p">
                {CLINIC_NAME} was founded with a single conviction: that patients deserve a healthcare environment that respects their time, comfort and dignity — and that practitioners deserve a space they can truly be proud of.
              </p>
              <p className="story-p">
                We set out to create a first-of-its-kind premium polyclinic in Siddipet — bringing together the finest medical professionals, state-of-the-art facilities, and an unwavering commitment to care, all under one roof.
              </p>
              <p className="story-p">
                Launching in 2026, {CLINIC_NAME} is more than a clinic. It is a new standard in healthcare.
              </p>
            </div>
            <div className="story-visual">
              <div className="story-stats">
                <div className="s-stat"><span className="s-stat-val">10+</span><span className="s-stat-lbl">Consult Rooms</span></div>
                <div className="s-stat"><span className="s-stat-val">All</span><span className="s-stat-lbl">Specialties</span></div>
                <div className="s-stat"><span className="s-stat-val">A–Z</span><span className="s-stat-lbl">Care Under One Roof</span></div>
                <div className="s-stat"><span className="s-stat-val">2026</span><span className="s-stat-lbl">Launching</span></div>
              </div>
              <div className="story-quote">
                <div className="story-quote-text">"Healthcare that honours both the healer and the patient."</div>
                <span className="story-quote-attr">— {CLINIC_NAME} Founding Vision</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section values-section">
        <div className="container">
          <span className="eyebrow">Our Values</span>
          <h2 className="section-title">What we stand for</h2>
          <div className="vals-grid">
            {VALUES.map(v => (
              <div key={v.title} className="val-card">
                <div className="val-icon">{v.icon}</div>
                <div className="val-title">{v.title}</div>
                <div className="val-desc">{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Launch */}
      <section className="section launch-section">
        <div className="container">
          <div className="launch-card">
            <div className="launch-badge"><span className="launch-dot" />Launching Soon · 2026</div>
            <div className="launch-title">Coming to Siddipet, 2026</div>
            <p className="launch-desc">Whether you are a patient looking for trusted care or a practitioner seeking a premium space — {CLINIC_NAME} is for you.</p>
            <button className="btn-terra" onClick={() => go('contact')}>Get in Touch</button>
            <div className="launch-contacts">
              <div><span className="lc-lbl">Email</span><span className="lc-val">{EMAIL}</span></div>
              <div><span className="lc-lbl">Phone</span><span className="lc-val">{PHONE_DISPLAY}</span></div>
              <div><span className="lc-lbl">Location</span><span className="lc-val">{CLINIC_LOCATION}, {CLINIC_STATE}</span></div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
